/**
 * Client Service
 * API calls for client dashboard, trainers, billing, and subscription management
 */

import api from './api';

const clientService = {
  // ==================== DASHBOARD ====================

  /**
   * Get client dashboard statistics
   * @returns {Promise} Dashboard stats including sessions, trainers, usage, and recent activity
   */
  getDashboardStats: async () => {
    return api.get('/client/dashboard');
  },

  // ==================== TRAINERS ====================

  /**
   * Get trainers for the organization
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search by name or email
   * @param {string} params.status - Filter by status (active, inactive, all)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of trainers with pagination
   */
  getTrainers: async (params = {}) => {
    return api.get('/client/trainers', params);
  },

  /**
   * Get a single trainer by ID
   * @param {string} trainerId - Trainer ID
   * @returns {Promise} Trainer details with performance stats
   */
  getTrainer: async (trainerId) => {
    return api.get(`/client/trainers/${trainerId}`);
  },

  /**
   * Get detailed trainer analytics
   * @param {string} trainerId - Trainer ID
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (7d, 30d, 90d, all)
   * @returns {Promise} Detailed analytics data including metrics, indices, trends, and feedback
   */
  getTrainerAnalytics: async (trainerId, params = {}) => {
    return api.get(`/client/trainers/${trainerId}/analytics`, params);
  },

  /**
   * Add a trainer directly
   * @param {Object} trainerData - Trainer details
   * @param {string} trainerData.email - Email address
   * @param {string} trainerData.firstName - First name
   * @param {string} trainerData.lastName - Last name
   * @param {string} trainerData.phone - Phone number (optional)
   * @param {string} trainerData.department - Department (optional)
   * @param {string} trainerData.jobTitle - Job title (optional)
   * @param {string} trainerData.password - Initial password
   * @returns {Promise} Created trainer
   */
  addTrainer: async (trainerData) => {
    return api.post('/client/trainers', trainerData);
  },

  /**
   * Invite a trainer via email
   * @param {Object} inviteData - Invitation details
   * @param {string} inviteData.email - Email address
   * @param {string} inviteData.firstName - First name (optional)
   * @param {string} inviteData.lastName - Last name (optional)
   * @param {string} inviteData.message - Personal message (optional)
   * @returns {Promise} Created invitation
   */
  inviteTrainer: async (inviteData) => {
    return api.post('/client/trainers/invite', inviteData);
  },

  /**
   * Cancel a trainer invitation
   * @param {string} invitationId - Invitation ID
   * @returns {Promise} Success response
   */
  cancelInvitation: async (invitationId) => {
    return api.delete(`/client/trainers/invitations/${invitationId}`);
  },

  /**
   * Remove a trainer from the organization
   * @param {string} trainerId - Trainer ID
   * @returns {Promise} Success response
   */
  removeTrainer: async (trainerId) => {
    return api.delete(`/client/trainers/${trainerId}`);
  },

  /**
   * Update a trainer's details
   * @param {string} trainerId - Trainer ID
   * @param {Object} trainerData - Updated trainer details
   * @param {string} trainerData.firstName - First name
   * @param {string} trainerData.lastName - Last name
   * @param {string} trainerData.phone - Phone number (optional)
   * @param {string} trainerData.jobTitle - Job title (optional)
   * @param {Array} trainerData.expertise - Areas of expertise (optional)
   * @param {string} trainerData.bio - Bio (optional)
   * @param {string} trainerData.status - Status (active/inactive)
   * @returns {Promise} Updated trainer
   */
  updateTrainer: async (trainerId, trainerData) => {
    return api.put(`/client/trainers/${trainerId}`, trainerData);
  },

  // ==================== SUBSCRIPTION ====================

  /**
   * Get subscription details
   * @returns {Promise} Subscription details with usage stats
   */
  getSubscription: async () => {
    return api.get('/client/subscription');
  },

  /**
   * Get current subscription
   * @returns {Promise} Current subscription tier and details
   */
  getCurrentSubscription: async () => {
    return api.get('/client/subscription/current');
  },

  /**
   * Get available subscription plans
   * @returns {Promise} List of available subscription plans
   */
  getSubscriptionPlans: async () => {
    return api.get('/client/subscription/plans');
  },

  /**
   * Update subscription (upgrade/downgrade)
   * @param {string} planId - Plan ID to switch to
   * @param {string} billingCycle - Billing cycle (monthly/annual)
   * @returns {Promise} Updated subscription
   */
  updateSubscription: async (planId, billingCycle = 'monthly') => {
    return api.put('/client/subscription', { planId, billingCycle });
  },

  /**
   * Cancel subscription
   * @param {string} reason - Cancellation reason (optional)
   * @returns {Promise} Updated subscription
   */
  cancelSubscription: async (reason = '') => {
    return api.post('/client/subscription/cancel', { reason });
  },

  /**
   * Reactivate a cancelled subscription
   * @returns {Promise} Updated subscription
   */
  reactivateSubscription: async () => {
    return api.post('/client/subscription/reactivate', {});
  },

  /**
   * Schedule a plan downgrade at end of billing period
   * @param {string} planId - Plan to downgrade to
   * @returns {Promise} Scheduled downgrade details
   */
  scheduleDowngrade: async (planId) => {
    return api.post('/client/subscription/downgrade', { planId });
  },

  /**
   * Cancel a scheduled downgrade
   * @returns {Promise} Success response
   */
  cancelScheduledDowngrade: async () => {
    return api.post('/client/subscription/downgrade/cancel', {});
  },

  /**
   * Create Dodo checkout session for subscription upgrade
   * @param {string} planId - Plan ID to upgrade to (pro, pro_plus, enterprise_starter, enterprise_scale)
   * @param {string} billingCycle - Billing cycle (monthly/yearly)
   * @returns {Promise} Checkout URL to redirect user to Dodo payment page
   */
  createCheckoutSession: async (planId, billingCycle = 'monthly') => {
    return api.post('/client/checkout', { planId, billingCycle });
  },

  /**
   * Manually sync subscription after payment (fallback if webhook fails)
   * @param {string} planId - Plan that was purchased
   * @param {string} billingCycle - Billing cycle (monthly/yearly)
   * @param {string} paymentId - Optional payment ID from Dodo
   * @returns {Promise} Updated subscription
   */
  syncSubscription: async (planId, billingCycle = 'monthly', paymentId = null) => {
    return api.post('/client/subscription/sync', { planId, billingCycle, paymentId });
  },

  // ==================== ADD-ONS ====================

  /**
   * Purchase trainer add-on for a specific trainer (₹1,000 for 30 days)
   * @param {string} trainerId - The trainer to activate via add-on
   * @returns {Promise} Checkout URL to redirect user to Dodo payment page
   */
  purchaseTrainerAddon: async (trainerId) => {
    return api.post(`/client/addon/trainer/${trainerId}`);
  },

  /**
   * Activate a trainer (within plan limit)
   * @param {string} trainerId - Trainer ID
   * @returns {Promise} Updated trainer data
   */
  activateTrainer: async (trainerId) => {
    return api.put(`/client/trainers/${trainerId}/activate`);
  },

  /**
   * Deactivate a trainer (mark as inactive)
   * @param {string} trainerId - Trainer ID
   * @returns {Promise} Updated trainer data
   */
  deactivateTrainer: async (trainerId) => {
    return api.put(`/client/trainers/${trainerId}/deactivate`);
  },

  // ==================== BILLING ====================

  /**
   * Get billing overview
   * @returns {Promise} Billing overview with subscription, payment methods, and recent invoices
   */
  getBillingOverview: async () => {
    return api.get('/client/billing');
  },

  /**
   * Get payment methods
   * @returns {Promise} List of payment methods
   */
  getPaymentMethods: async () => {
    return api.get('/client/payment-methods');
  },

  /**
   * Add a payment method
   * @param {Object} paymentData - Payment method details
   * @param {string} paymentData.type - Type (card, upi, bank_transfer)
   * @param {Object} paymentData.card - Card details (if type is card)
   * @param {Object} paymentData.upi - UPI details (if type is upi)
   * @param {string} paymentData.billingName - Billing name
   * @param {Object} paymentData.billingAddress - Billing address
   * @param {boolean} paymentData.setDefault - Set as default payment method
   * @returns {Promise} Created payment method
   */
  addPaymentMethod: async (paymentData) => {
    return api.post('/client/payment-methods', paymentData);
  },

  /**
   * Set default payment method
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise} Updated payment method
   */
  setDefaultPaymentMethod: async (paymentMethodId) => {
    return api.put(`/client/payment-methods/${paymentMethodId}/default`, {});
  },

  /**
   * Delete a payment method
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise} Success response
   */
  deletePaymentMethod: async (paymentMethodId) => {
    return api.delete(`/client/payment-methods/${paymentMethodId}`);
  },

  // ==================== INVOICES ====================

  /**
   * Get invoices
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (pending, paid, failed, all)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of invoices with pagination
   */
  getInvoices: async (params = {}) => {
    return api.get('/client/invoices', params);
  },

  /**
   * Get single invoice
   * @param {string} invoiceId - Invoice ID
   * @returns {Promise} Invoice details
   */
  getInvoice: async (invoiceId) => {
    return api.get(`/client/invoices/${invoiceId}`);
  },

  // ==================== ANALYTICS ====================

  /**
   * Get client analytics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (7d, 30d, 90d, 12m)
   * @returns {Promise} Analytics data
   */
  getAnalytics: async (params = {}) => {
    return api.get('/client/analytics', params);
  },

  /**
   * Phase 2 Step 9: post-session / manager / reflection aggregates for this org
   */
  getPhase2Analytics: async () => {
    return api.get('/client/analytics/phase2');
  },

  // ==================== REPORTS ====================

  /**
   * Get reports for the organization
   * @param {Object} params - Query parameters
   * @param {string} params.type - Filter by type (summary, session, analytics, participant, trainer, all)
   * @param {string} params.search - Search by title or description
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of reports with stats and pagination
   */
  getReports: async (params = {}) => {
    return api.get('/client/reports', params);
  },

  // ==================== WORKBOOKS ====================

  /**
   * Get all workbooks from organization's sessions
   * @param {Object} params - Query parameters
   * @param {string} params.sessionId - Filter by session ID (optional)
   * @param {string} params.search - Search by name (optional)
   * @returns {Promise} List of workbooks with session info
   */
  getWorkbooks: async (params = {}) => {
    return api.get('/client/workbooks', params);
  },

  /**
   * Upload workbook file to server
   * @param {string} sessionId - Session ID
   * @param {File} file - File to upload
   * @returns {Promise} Uploaded file data with permanent URL
   */
  uploadWorkbook: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    const uploadResult = await api.upload(`/upload/session/${sessionId}/document`, formData);

    // Ensure we have a valid URL from the upload response
    const url = uploadResult?.data?.url;
    if (!url) {
      console.error('Upload failed - no URL returned:', uploadResult);
      throw new Error('Upload failed - no URL returned from server');
    }

    return {
      success: true,
      data: {
        name: file.name,
        url: url,
        type: file.type || file.name.split('.').pop() || 'unknown',
        size: file.size,
        uploadedAt: new Date().toISOString(),
      },
    };
  },
};

export default clientService;
